import format from 'date-fns/format';
import React, { forwardRef, useCallback } from 'react';
import ReactDatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { TFormFieldsMailSubscription } from '@/shared/api/mailSubscription/types';
import type { TError } from '@/shared/components/ErrorMessage/types';
import { FORMAT_DATE_TIME } from '@/shared/constants';

import { InputForBirthday } from '../Input';
import { Button, CustomHeaderTitleTemplate, CustomHeaderWrapper } from './parts';

const CustomHeader: React.FC<ReactDatePickerCustomHeaderProps> = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => {
  const { resources } = window.mailSubscription || {};

  const messages = {
    previosMonth:
      resources?.['Accounts.MailSubscriprion.Fields.Birthday.Datepicker.Header.PreviousMonth'],
    nextMonth: resources?.['Accounts.MailSubscriprion.Fields.Birthday.Datepicker.Header.NextMonth'],
  };
  const month = format(date, FORMAT_DATE_TIME.MMMM);
  return (
    <CustomHeaderWrapper>
      <Button
        type="button"
        className="react-datepicker__navigation react-datepicker__navigation--previous"
        aria-label={messages.previosMonth}
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
      >
        <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
          {messages.previosMonth}
        </span>
      </Button>
      <CustomHeaderTitleTemplate>{month}</CustomHeaderTitleTemplate>

      <Button
        type="button"
        className="react-datepicker__navigation react-datepicker__navigation--next"
        aria-label={messages.nextMonth}
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
      >
        <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
          {messages.nextMonth}
        </span>
      </Button>
    </CustomHeaderWrapper>
  );
};

const BirthdayField: React.FC = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<TFormFieldsMailSubscription>();

  const { formatMessage } = useIntl();

  const messages = {
    birthday: formatMessage({ id: 'BirthdayLabel' }),
    birthdayPlaceholder: formatMessage({ id: 'BirthdayPlaceholder' }),
  };

  const { MMdd } = FORMAT_DATE_TIME;

  const ForwardInputForBirthday = useCallback(
    (props: { [x: string]: unknown }, ref: React.ForwardedRef<HTMLInputElement> | undefined) => (
      <InputForBirthday
        {...props}
        className="r-relative"
        label={messages.birthday}
        id="birthday"
        placeholder={messages.birthdayPlaceholder}
        error={errors.Birthday as TError}
        refInput={ref}
      />
    ),
    [errors.Birthday, messages.birthday, messages.birthdayPlaceholder],
  );
  const CustomInput = forwardRef<HTMLInputElement>(ForwardInputForBirthday);

  const handlerOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
    }
  };

  return (
    <div className="r-w-full">
      <Controller
        control={control}
        name="Birthday"
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <ReactDatePicker
            className="birthdayField"
            renderCustomHeader={CustomHeader}
            selected={value ? new Date(value) : null}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={handlerOnKeyDown}
            placeholderText={messages.birthdayPlaceholder}
            dateFormat={MMdd}
            customInput={<CustomInput />}
            ref={ref}
          />
        )}
      />
    </div>
  );
};

export default BirthdayField;
